import React, { useRef, useState } from 'react';
import FileSaver from 'file-saver';

import { StateLanguage } from '../../../../languages/config/StateLanguage';
import { attachmentPdfFilename } from '../../../../scripts/document.filename.script';
import { ProviderOrder } from '../../../../models/provider.order.model';
import { ProviderInvoice } from '../../../../models/provider.invoice.model';
import { Expense } from '../../../../models/expense.model';
import { Voucher } from '../../../../models/voucher.model';
import { MediaDA } from '../../../../services/admin/media.service';
import { QuoteDA } from '../../../../services/admin/quote.service';
import { OrderDA } from '../../../../services/admin/order.service';
import { InvoiceDA } from '../../../../services/admin/invoice.service';
import { ProviderOrderDA } from '../../../../services/admin/provider.order.service';
import { ProviderInvoiceDA } from '../../../../services/admin/provider.invoice.service';
import { ExpenseDA } from '../../../../services/admin/expense.service';
import { AccountingEntryDA } from '../../../../services/admin/accounting.entry.service';
import { datetimeFormat12h } from '../../../../scripts/date.time.format.script';
import { suffixLegend } from '../../../../scripts/name.legend.script';
import { modalShow } from '../../../../utils/modal.util';
import { GlobalSwal } from '../../../../styles/swal.style';
import { getError } from '../../../../languages/translations/response';
import { errorWebHandler } from '../../../../scripts/error.web.handler.script';

import AppModalAttachDocument from '../../../../components/modal/ModalAttachDocument';
import { VoucherEntityType } from '../../../../components/modal/ModalAttachDocument';

export interface AppPurchaseViewVouchersProps {
  purchase: string,
  record: any | ProviderOrder | ProviderInvoice | Expense,
  entity_type?: VoucherEntityType,
  id_entity?: string,
  id_company?: string,
  showVoucherActions?: boolean,
  showDeleteAction?: boolean,
  onVouchersChanged?: (updatedEntity: any) => void
};

function uploadVoucherByEntityType(entity_type: VoucherEntityType, id_company: string, id_entity: string, file: File) {
  switch (entity_type) {
    case 'quote':            return QuoteDA.uploadVoucher(id_company, id_entity, file)
    case 'order':            return OrderDA.uploadVoucher(id_company, id_entity, file)
    case 'invoice':          return InvoiceDA.uploadVoucher(id_company, id_entity, file)
    case 'provider-order':   return ProviderOrderDA.uploadVoucher(id_company, id_entity, file)
    case 'provider-invoice': return ProviderInvoiceDA.uploadVoucher(id_company, id_entity, file)
    case 'expense':          return ExpenseDA.uploadVoucher(id_company, id_entity, file)
    case 'accounting-entry': return AccountingEntryDA.uploadVoucher(id_company, id_entity, file)
  }
}

function deleteVoucherByEntityType(entity_type: VoucherEntityType, id_company: string, id_entity: string, index: number) {
  switch (entity_type) {
    case 'quote':            return QuoteDA.deleteVoucher(id_company, id_entity, index)
    case 'order':            return OrderDA.deleteVoucher(id_company, id_entity, index)
    case 'invoice':          return InvoiceDA.deleteVoucher(id_company, id_entity, index)
    case 'provider-order':   return ProviderOrderDA.deleteVoucher(id_company, id_entity, index)
    case 'provider-invoice': return ProviderInvoiceDA.deleteVoucher(id_company, id_entity, index)
    case 'expense':          return ExpenseDA.deleteVoucher(id_company, id_entity, index)
    case 'accounting-entry': return AccountingEntryDA.deleteVoucher(id_company, id_entity, index)
  }
}

const AppPurchaseViewVouchers: React.FunctionComponent<AppPurchaseViewVouchersProps> = ({purchase, record, entity_type, id_entity, id_company, showVoucherActions, showDeleteAction, onVouchersChanged}) => {
  const {lang} = StateLanguage()

  const [loadingIndex, setLoadingIndex] = useState<number | null>(null)
  const [uploading, setUploading] = useState(false)
  const hiddenFileInputRef = useRef<HTMLInputElement>(null)

  function voucherName(item: Voucher): string {
    return item.original_name ?? item.name ?? 'Attachment'
  }

  function voucherDate(item: Voucher): string {
    return datetimeFormat12h(lang.code, item.attached_at ?? item.created_at ?? '')
  }

  function formatFileSize(bytes: number): string {
    if (!bytes || bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'Ko', 'Mo', 'Go']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
  }

  const executeOpenVoucher = async (item: Voucher, index: number) => {
    setLoadingIndex(index)

    if (item.id_document_inbox && id_company) {
      const signedUrl = await MediaDA.getDocumentSignedUrl(id_company, item.id_document_inbox)
      if (signedUrl) {
        window.open(signedUrl, '_blank')
      }
    } else if (item.file) {
      window.open(item.file, '_blank')
    }

    setLoadingIndex(null)
  }

  const executeDownloadFileVoucher = async (item: Voucher, index: number) => {
    let element: HTMLElement = document.getElementById(`button-download-file-voucher${suffixLegend(index)}`) as HTMLElement
    element.setAttribute('data-kt-indicator', 'on')

    if (item.id_document_inbox && id_company) {
      const signedUrl = await MediaDA.getDocumentSignedUrl(id_company, item.id_document_inbox)
      if (signedUrl) {
        FileSaver.saveAs(signedUrl, attachmentPdfFilename(voucherName(item), 'voucher'))
      }
    } else if (item.file) {
      FileSaver.saveAs(item.file, attachmentPdfFilename(item.name, 'voucher'))
    }

    setTimeout(() => {
      element.setAttribute('data-kt-indicator', 'off')
    }, 500)
  }

  const handleUploadNewPdf = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !entity_type || !id_entity || !id_company) return
    e.target.value = ''

    setUploading(true)
    try {
      const response = await uploadVoucherByEntityType(entity_type, id_company, id_entity, file)
      if (response?.status === 200 || response?.status === 201) {
        const updated = response.data?.data ?? response.data ?? response
        onVouchersChanged?.(updated)
      } else {
        GlobalSwal.fire({
          title: getError(response?.data?.code, lang.code, response?.data?.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    } catch (error) {
      errorWebHandler(lang, error)
    } finally {
      setUploading(false)
    }
  }

  const handleDeleteVoucher = async (index: number) => {
    if (!entity_type || !id_entity || !id_company) return

    const result = await GlobalSwal.fire({
      text: lang.labels.confirmRemoveVoucher,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: lang.labels.yesConfirm,
      cancelButtonText: lang.labels.noCancel
    })

    if (!result.isConfirmed) return

    try {
      const response = await deleteVoucherByEntityType(entity_type, id_company, id_entity, index)
      if (response?.status === 200) {
        const updated = response.data?.data ?? response.data ?? response
        onVouchersChanged?.(updated)
      } else {
        GlobalSwal.fire({
          title: getError(response?.data?.code, lang.code, response?.data?.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    } catch (error) {
      errorWebHandler(lang, error)
    }
  }

  return (
    <>
      <div className={`card card-flush ${purchase === "expense" ? "mb-0" : "mb-5"}`}>
        <div className="card-header">
          <div className="card-title">
            <h4>{lang.labels.vouchers}</h4>
          </div>
          { showVoucherActions && entity_type && id_entity && id_company &&
            <div className="card-toolbar">
              <button className="btn btn-sm btn-light-primary me-2" type="button" onClick={() => modalShow('modal-attach-document')}>
                <span className="svg-icon svg-icon-3 me-1">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path opacity="0.3" d="M19 22H5C4.4 22 4 21.6 4 21V3C4 2.4 4.4 2 5 2H14L20 8V21C20 21.6 19.6 22 19 22ZM16 13H13V10C13 9.4 12.6 9 12 9C11.4 9 11 9.4 11 10V13H8C7.4 13 7 13.4 7 14C7 14.6 7.4 15 8 15H11V18C11 18.6 11.4 19 12 19C12.6 19 13 18.6 13 18V15H16C16.6 15 17 14.6 17 14C17 13.4 16.6 13 16 13Z" fill="black" />
                    <path d="M15 8H20L14 2V7C14 7.6 14.4 8 15 8Z" fill="black" />
                  </svg>
                </span>
                {lang.labels.attachExistingDocument}
              </button>
              <button className="btn btn-sm btn-light-primary" type="button" disabled={uploading} onClick={() => hiddenFileInputRef.current?.click()}>
                { uploading
                  ?
                  <span className="spinner-border spinner-border-sm align-middle me-1"></span>
                  :
                  <span className="svg-icon svg-icon-3 me-1">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path opacity="0.3" d="M19 15C19 17.2 17.2 19 15 19H5C2.8 19 1 17.2 1 15C1 13.2 2.2 11.6 4 11.1C3.4 10.4 3 9.5 3 8.5C3 6.6 4.6 5 6.5 5C7.1 5 7.7 5.1 8.2 5.4C9 3.4 11 2 13.5 2C16.5 2 19 4.5 19 7.5C19 8 18.9 8.5 18.8 9C20.6 9.5 22 11.1 22 13C22 15.2 20.2 17 18 17H17" fill="black"/>
                      <path d="M12 8L7 13H10V18H14V13H17L12 8Z" fill="black"/>
                    </svg>
                  </span>
                }
                {lang.labels.uploadNewPdf}
              </button>
              <input
                ref={hiddenFileInputRef}
                type="file"
                accept="application/pdf,image/*"
                style={{ display: 'none' }}
                onChange={handleUploadNewPdf}
              />
            </div>
          }
        </div>
        <div className="card-body pt-0">
          <div className="dataTables_wrapper">
            <div className="table-responsive">
              <table className="table align-middle table-row-dashed gy-2 mb-2">
                <thead className="border-bottom">
                  <tr className="text-muted text-uppercase fw-bolder fs-7">
                    <th className="min-w-200px text-nowrap text-start">{lang.labels.name}</th>
                    <th className="min-w-100px text-nowrap text-start">{lang.labels.size}</th>
                    <th className="min-w-100px text-nowrap text-start">{lang.labels.lastModified}</th>
                    <th className="w-75px text-nowrap text-end">{lang.labels.download}</th>
                    { showDeleteAction &&
                      <th className="w-50px text-nowrap text-end"></th>
                    }
                  </tr>
                </thead>
                { (record.vouchers ?? []).length > 0
                  ?
                  <tbody className="text-secondary fw-bold">
                    { (record.vouchers ?? []).map (( (item: Voucher, index: number) => { return (
                      <tr key={index} className="row-dashed">
                        <td>
                          { item.file_key || item.id_document_inbox
                            ?
                            <div className="d-flex align-items-center cursor-pointer" onClick={() => executeOpenVoucher(item, index)}>
                              <span className="svg-icon svg-icon-dark svg-icon-2 p-1 me-1">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path opacity="0.3" d="M19 22H5C4.4 22 4 21.6 4 21V3C4 2.4 4.4 2 5 2H14L20 8V21C20 21.6 19.6 22 19 22ZM15 17C15 16.4 14.6 16 14 16H8C7.4 16 7 16.4 7 17C7 17.6 7.4 18 8 18H14C14.6 18 15 17.6 15 17ZM17 12C17 11.4 16.6 11 16 11H8C7.4 11 7 11.4 7 12C7 12.6 7.4 13 8 13H16C16.6 13 17 12.6 17 12ZM17 7C17 6.4 16.6 6 16 6H8C7.4 6 7 6.4 7 7C7 7.6 7.4 8 8 8H16C16.6 8 17 7.6 17 7Z" fill="black"/>
                                  <path d="M15 8H20L14 2V7C14 7.6 14.4 8 15 8Z" fill="black"/>
                                </svg>
                              </span>
                              <span className="fw-bolder text-hover-primary">
                                {loadingIndex === index
                                  ? <span className="spinner-border spinner-border-sm align-middle"></span>
                                  : voucherName(item)
                                }
                              </span>
                            </div>
                            :
                            item.file
                            ?
                            <div className="d-flex align-items-center cursor-pointer" data-bs-toggle="modal" data-bs-target={`#voucher-pdf-${index}`}>
                              <span className="svg-icon svg-icon-dark svg-icon-2 p-1 me-1">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path opacity="0.3" d="M19 22H5C4.4 22 4 21.6 4 21V3C4 2.4 4.4 2 5 2H14L20 8V21C20 21.6 19.6 22 19 22ZM15 17C15 16.4 14.6 16 14 16H8C7.4 16 7 16.4 7 17C7 17.6 7.4 18 8 18H14C14.6 18 15 17.6 15 17ZM17 12C17 11.4 16.6 11 16 11H8C7.4 11 7 11.4 7 12C7 12.6 7.4 13 8 13H16C16.6 13 17 12.6 17 12ZM17 7C17 6.4 16.6 6 16 6H8C7.4 6 7 6.4 7 7C7 7.6 7.4 8 8 8H16C16.6 8 17 7.6 17 7Z" fill="black"/>
                                  <path d="M15 8H20L14 2V7C14 7.6 14.4 8 15 8Z" fill="black"/>
                                </svg>
                              </span>
                              <span className="fw-bolder text-hover-primary">{voucherName(item)}</span>
                            </div>
                            :
                            <div className="d-flex align-items-center">
                              <span className="svg-icon svg-icon-muted svg-icon-2 p-1 me-1">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path opacity="0.3" d="M19 22H5C4.4 22 4 21.6 4 21V3C4 2.4 4.4 2 5 2H14L20 8V21C20 21.6 19.6 22 19 22ZM15 17C15 16.4 14.6 16 14 16H8C7.4 16 7 16.4 7 17C7 17.6 7.4 18 8 18H14C14.6 18 15 17.6 15 17ZM17 12C17 11.4 16.6 11 16 11H8C7.4 11 7 11.4 7 12C7 12.6 7.4 13 8 13H16C16.6 13 17 12.6 17 12ZM17 7C17 6.4 16.6 6 16 6H8C7.4 6 7 6.4 7 7C7 7.6 7.4 8 8 8H16C16.6 8 17 7.6 17 7Z" fill="black"/>
                                  <path d="M15 8H20L14 2V7C14 7.6 14.4 8 15 8Z" fill="black"/>
                                </svg>
                              </span>
                              <span className="text-gray-400">{voucherName(item)}</span>
                            </div>
                          }
                          { item.file &&
                            <div id={`voucher-pdf${suffixLegend(index)}`} className="modal fade" tabIndex={-1} aria-hidden="true">
                              <div className="modal-dialog modal-dialog-centered">
                                <div className="modal-content">
                                  <iframe id="modal-voucher-view-container" title={voucherName(item)} src={item.file} width="100%" height="100%" />
                                </div>
                              </div>
                            </div>
                          }
                        </td>
                        <td>{formatFileSize(item.size)}</td>
                        <td>{voucherDate(item)}</td>
                        <td valign="middle" className="text-end p-0">
                          { (item.file_key || item.id_document_inbox || item.file)
                            ?
                            <button id={`button-download-file-voucher${suffixLegend(index)}`} className="btn btn-sm btn-icon btn-flex btn-bg-light btn-active-color-success h-30px w-30px" type="button" data-kt-indicator="off" onClick={() => executeDownloadFileVoucher(item, index)}>
                              <span className="indicator-label">
                                <span className="svg-icon svg-icon-primary svg-icon-3">
                                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path opacity="0.3" d="M5 15C3.3 15 2 13.7 2 12C2 10.3 3.3 9 5 9H5.10001C5.00001 8.7 5 8.3 5 8C5 5.2 7.2 3 10 3C11.9 3 13.5 4 14.3 5.5C14.8 5.2 15.4 5 16 5C17.7 5 19 6.3 19 8C19 8.4 18.9 8.7 18.8 9C18.9 9 18.9 9 19 9C20.7 9 22 10.3 22 12C22 13.7 20.7 15 19 15H5ZM5 12.6H13L9.7 9.29999C9.3 8.89999 8.7 8.89999 8.3 9.29999L5 12.6Z" fill="black"/>
                                    <path d="M17 17.4V12C17 11.4 16.6 11 16 11C15.4 11 15 11.4 15 12V17.4H17Z" fill="black"/>
                                    <path opacity="0.3" d="M12 17.4H20L16.7 20.7C16.3 21.1 15.7 21.1 15.3 20.7L12 17.4Z" fill="black"/>
                                    <path d="M8 12.6V18C8 18.6 8.4 19 9 19C9.6 19 10 18.6 10 18V12.6H8Z" fill="black"/>
                                  </svg>
                                </span>
                              </span>
                              <span className="indicator-progress">
                                <span className="spinner-border spinner-border-sm align-middle"></span>
                              </span>
                            </button>
                            :
                            <span className="text-gray-400">—</span>
                          }
                        </td>
                        { showDeleteAction &&
                          <td valign="middle" className="text-end p-0">
                            <button className="btn btn-sm btn-icon btn-flex btn-bg-light btn-active-color-danger h-30px w-30px" type="button" title={lang.labels.removeDocument} onClick={() => handleDeleteVoucher(index)}>
                              <span className="svg-icon svg-icon-danger svg-icon-3">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                  <path d="M5 9C5 8.44772 5.44772 8 6 8H18C18.5523 8 19 8.44772 19 9V18C19 19.6569 17.6569 21 16 21H8C6.34315 21 5 19.6569 5 18V9Z" fill="black" />
                                  <path opacity="0.5" d="M5 5C5 4.44772 5.44772 4 6 4H18C18.5523 4 19 4.44772 19 5V5C19 5.55228 18.5523 6 18 6H6C5.44772 6 5 5.55228 5 5V5Z" fill="black" />
                                  <path opacity="0.5" d="M9 4C9 3.44772 9.44772 3 10 3H14C14.5523 3 15 3.44772 15 4V4H9V4Z" fill="black" />
                                </svg>
                              </span>
                            </button>
                          </td>
                        }
                      </tr>
                    )}))}
                  </tbody>
                  :
                  <tbody>
                    <tr className="text-secondary fw-bold">
                      <td colSpan={showDeleteAction ? 5 : 4} className="text-center">{lang.labels.noRecordsFound}</td>
                    </tr>
                  </tbody>
                }
              </table>
            </div>
          </div>
        </div>
      </div>
      { showVoucherActions && entity_type && id_entity && id_company &&
        <AppModalAttachDocument
          id_company={id_company}
          entity_type={entity_type}
          id_entity={id_entity}
          onAttached={(updated) => onVouchersChanged?.(updated)}
        />
      }
    </>
  )
};

export default AppPurchaseViewVouchers;
